import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Tv, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * DeviceLink — mobile/desktop page where a signed-in user types the 6-char
 * code shown on their Smart TV to pair it with their account.
 *
 * Users land here two ways:
 *   1. They navigate to `/link` from the homepage.
 *   2. They scan the QR on their TV, which deep-links `/link?code=ABCD12`.
 *
 * If the user isn't signed in yet, we redirect to /login and bounce back.
 */
export default function DeviceLink() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initialCode = (new URLSearchParams(location.search).get("code") || "").toUpperCase();
  const [code, setCode] = useState(initialCode);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      // Bounce through login, returning to this page with code preserved
      navigate(`/login?redirect=${encodeURIComponent(`/link${initialCode ? `?code=${initialCode}` : ""}`)}`);
    }
  }, [loading, user, navigate, initialCode]);

  const submit = async (e) => {
    e?.preventDefault();
    const c = (code || "").trim().toUpperCase();
    if (c.length < 6) { toast.error("Enter the 6-character code shown on your TV"); return; }
    setSubmitting(true);
    try {
      const label = navigator.userAgent?.slice(0, 40) || "Mobile";
      await api.post("/device-link/claim", { code: c, device_label: label });
      setDone(true);
      toast.success("Your TV is now signed in to Tap IN TV");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen pt-32 pb-16 px-4">
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-tv-accent/20 flex items-center justify-center">
            <Tv className="w-6 h-6 text-tv-accent" />
          </div>
          <div>
            <h1 className="font-display font-black text-3xl text-white">Pair your TV</h1>
            <p className="text-white/60 text-sm">Sign in to Tap IN TV on your Smart TV in seconds.</p>
          </div>
        </div>

        {done ? (
          <div
            data-testid="device-link-done"
            className="bg-emerald-500/10 border border-emerald-500/30 rounded-3xl p-8 text-center"
          >
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <div className="font-display font-black text-2xl text-white mb-2">All set!</div>
            <p className="text-white/70 mb-6">Your TV is now signed in as <span className="text-tv-accent font-bold">{user.name || user.email}</span>.</p>
            <Link
              to="/"
              data-testid="device-link-home"
              className="inline-flex items-center gap-2 bg-tv-accent hover:bg-yellow-400 text-black font-bold px-6 py-3 rounded-full"
            >
              Back to home <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white/5 border border-white/10 rounded-3xl p-6 space-y-5">
            <div>
              <label className="text-white/60 text-sm font-bold uppercase tracking-wider mb-2 block">Code from your TV</label>
              <input
                data-testid="device-link-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7))}
                placeholder="ABCD23"
                autoComplete="off"
                autoFocus
                className="w-full bg-black/40 border-2 border-white/20 rounded-2xl px-6 py-5 text-white font-display font-black text-4xl tracking-[0.3em] text-center outline-none focus:border-tv-accent uppercase"
              />
              <div className="text-white/40 text-xs mt-2">Look for the 6-character code on your TV's Profile tab.</div>
            </div>
            <button
              type="submit"
              disabled={submitting || code.length < 6}
              data-testid="device-link-submit"
              className="w-full bg-tv-accent hover:bg-yellow-400 disabled:opacity-40 disabled:hover:bg-tv-accent text-black font-bold text-lg py-4 rounded-full transition"
            >
              {submitting ? "Pairing…" : "Sign in my TV"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
